if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      this.abortController = null;
      this.requestId = 0;
    }

    hide(preventFocus = false) {
      this.requestId += 1;
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      this.setOpenerLoading(this.currentOpener, false);
      this.currentOpener = null;

      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification && typeof cartNotification.setActiveElement === 'function') {
        cartNotification.setActiveElement(this.openedBy);
      }
      if (this.modalContent) this.modalContent.innerHTML = '';

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    setOpenerLoading(opener, isLoading) {
      if (!opener) return;
      const spinner = opener.querySelector('.loading-overlay__spinner');

      if (isLoading) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        if (spinner) spinner.classList.remove('hidden');
        return;
      }

      opener.removeAttribute('aria-disabled');
      opener.classList.remove('loading');
      if (spinner) spinner.classList.add('hidden');
    }

    show(opener) {
      this.setOpenerLoading(opener, true);
      this.currentOpener = opener;

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const requestId = ++this.requestId;
      const productUrl = opener && opener.getAttribute('data-product-url');

      if (!productUrl) {
        this.showLoadError(opener);
        this.setOpenerLoading(opener, false);
        return;
      }

      // Only same-origin product paths — prevents open redirect / remote HTML injection.
      let safeProductUrl = '';
      try {
        const parsed = new URL(productUrl, window.location.origin);
        if (parsed.origin === window.location.origin && parsed.pathname.charAt(0) === '/') {
          safeProductUrl = parsed.pathname + parsed.search;
        }
      } catch (e) {
        safeProductUrl = '';
      }
      if (!safeProductUrl) {
        this.showLoadError(opener);
        this.setOpenerLoading(opener, false);
        return;
      }

      fetch(safeProductUrl, { signal: this.abortController.signal })
        .then((response) => {
          if (!response.ok) throw new Error(String(response.status));
          return response.text();
        })
        .then((responseText) => {
          if (requestId !== this.requestId) return;

          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');

          if (!this.productElement || !this.modalContent) {
            this.showLoadError(opener);
            return;
          }

          this.preventDuplicatedIDs();
          this.removeDOMElements();
          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.preventVariantURLSwitching();
          this.primeGallery();
          super.show(opener);
        })
        .catch((error) => {
          if (error && error.name === 'AbortError') return;
          if (requestId !== this.requestId) return;
          this.showLoadError(opener);
        })
        .finally(() => {
          if (opener !== this.currentOpener || requestId === this.requestId) {
            this.setOpenerLoading(opener, false);
          }
        });
    }

    showLoadError(opener) {
      if (!this.modalContent) return;
      const message = (window.cartStrings && window.cartStrings.error) || 'Unable to load product.';
      this.modalContent.innerHTML = `<p class="quick-add-modal__error" role="alert">${message}</p>`;
      super.show(opener);
    }

    setInnerHTML(element, html) {
      if (!element) return;
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        if (oldScriptTag.parentNode) {
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        }
      });
    }

    preventVariantURLSwitching() {
      const variantPicker = this.modalContent && this.modalContent.querySelector('variant-radios,variant-selects');
      if (variantPicker) variantPicker.setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
      if (!this.productElement) return;

      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();
    }

    preventDuplicatedIDs() {
      if (!this.productElement) return;
      const sectionId = this.productElement.dataset.section;
      if (!sectionId) return;

      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios').forEach((variantSelect) => {
        variantSelect.dataset.originalSection = sectionId;
      });
    }

    /* Native lazy-loading lags inside the horizontally scrolling modal gallery,
       so slides flashed blank. Thumbs load up front (they are small); main
       images load once they are within one slide of the viewport. */
    primeGallery() {
      if (!this.modalContent) return;
      this.modalContent
        .querySelectorAll('.thumbnail-list img[loading="lazy"]')
        .forEach((img) => img.setAttribute('loading', 'eager'));

      const list = this.modalContent.querySelector('.product__media-list');
      if (!list) return;
      const slides = list.querySelectorAll(':scope > .product__media-item');
      slides.forEach((slide, i) => {
        if (i < 2) slide.querySelectorAll('img').forEach((img) => img.setAttribute('loading', 'eager'));
      });
      if (!('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.querySelectorAll('img[loading="lazy"]').forEach((img) => img.setAttribute('loading', 'eager'));
            io.unobserve(entry.target);
          });
        },
        { root: list, rootMargin: '0px 100% 0px 100%' }
      );
      slides.forEach((slide) => io.observe(slide));
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent && this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }
  });
}
